import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import AdminWelcomeHeader from './components/AdminWelcomeHeader';
import AdminQuickActions from './components/AdminQuickActions';
import AdminMetrics from './components/AdminMetrics';
import EmployeeManagement from './components/EmployeeManagement';
import AdminNotificationCenter from './components/AdminNotificationCenter';
import HROperations from './components/HROperations';
import SystemOverview from './components/SystemOverview';
import HRReportsSalarySlips from './components/HRReportsSalarySlips';
import PublicHolidayManagement from './components/PublicHolidayManagement';

// PERFORMANCE FIX: Move ErrorFallback outside component to prevent re-creation
const ErrorFallback = memo(({ error, resetError }) => (
  <div className="flex items-center justify-center min-h-96 bg-card rounded-lg border border-border p-6">
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
        <span className="text-2xl text-destructive">⚠️</span>
      </div>
      <h2 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h2>
      <p className="text-muted-foreground mb-4">
        There was an error loading this section of the admin dashboard.
      </p>
      <div className="space-x-2">
        <button
          onClick={resetError}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          Try Again
        </button>
        <button
          onClick={() => window.location?.reload()}
          className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  </div>
));

// PERFORMANCE FIX: Simple wrapper - no useEffect that causes re-renders
const SafeWrapper = memo(({ children }) => (
  <div>{children}</div>
));

const AdminDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [renderingError, setRenderingError] = useState(null);
  const location = useLocation();

  // Handle URL query parameters for tab navigation
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(location?.search);
      const tabParam = urlParams?.get('tab');
      console.log('URL tab parameter:', tabParam);
      console.log('Current location:', location?.pathname, location?.search);
      if (tabParam) {
        setActiveTab(tabParam);
      }
      // PERFORMANCE FIX: Don't set default here, it causes extra re-renders
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
    }
  }, [location?.search]);

  // PERFORMANCE FIX: Memoize tab content to prevent unnecessary re-renders
  const renderTabContent = useCallback(() => {
    try {
      switch (activeTab) {
        case 'employees':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">Employee Management</h1>
              </div>
              <EmployeeManagement />
            </div>
          );
        
        case 'leave-management':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">Leave Management</h1>
              </div>
              <HROperations />
            </div>
          );

        case 'public-holidays':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">Public Holiday Management</h1>
              </div>
              <PublicHolidayManagement />
            </div>
          );

        case 'payroll':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">Payroll Management</h1>
              </div>
              <HROperations />
            </div>
          );
        
        case 'hr-reports':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">HR Reports & Analytics</h1>
              </div>
              <HRReportsSalarySlips />
              <AdminMetrics />
              <SystemOverview />
            </div>
          );
        
        case 'system-overview':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-foreground">System Overview</h1>
              </div>
              <SystemOverview />
              <AdminMetrics />
            </div>
          );
        
        default:
          return (
            <div className="space-y-6">
              {/* Admin Welcome Header */}
              <AdminWelcomeHeader />
              
              {/* Admin Quick Actions */}
              <AdminQuickActions />
              
              {/* Admin Metrics Overview */}
              <AdminMetrics />
              
              {/* Admin Notifications */}
              <AdminNotificationCenter />
              
              {/* Main Admin Content Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                {/* Left Column - Employee Management */}
                <div className="xl:col-span-2 space-y-6">
                  <EmployeeManagement />
                  <HROperations />
                </div>
                
                {/* Right Column - System Overview */}
                <div className="space-y-6">
                  <SystemOverview />
                </div>
              </div>
            </div>
          );
      }
    } catch (error) {
      console.error('Error rendering tab content:', error);
      setRenderingError(error);
      return (
        <ErrorFallback 
          error={error} 
          resetError={() => setRenderingError(null)}
        />
      );
    }
  }, [activeTab]);

  // If there's a critical rendering error, show the fallback
  if (renderingError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <ErrorFallback 
          error={renderingError} 
          resetError={() => {
            setRenderingError(null);
            window.location?.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed}
        onToggle={(collapsed) => setSidebarCollapsed(collapsed)} 
        isMobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <Header 
        sidebarCollapsed={sidebarCollapsed}
        onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
      />
      
      <main className={`pt-16 transition-smooth ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-240'} ml-0`}>
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
          {renderTabContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;